export const GOOGLE_PASSWORD_PROMPT_KEY = 'seller_google_password_prompt';

/** Login failed because the account has no password (typical Google sign-up on buyer site). */
export function isGoogleAccountNoPasswordError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;

  const e = err as {
    message?: string;
    response?: { status?: number; data?: { message?: string; status?: string } | string };
    config?: { url?: string };
  };

  if (e.message === 'NO_PASSWORD') return true;

  let apiMessage = '';
  const data = e.response?.data;
  if (typeof data === 'string') {
    apiMessage = data;
  } else if (data && typeof data === 'object') {
    apiMessage = String(data.message || '');
  }
  apiMessage = apiMessage || String(e.message || '');
  const normalized = apiMessage.toLowerCase();

  if (normalized.includes('something went very wrong')) return true;
  if (normalized.includes('illegal arguments')) return true;
  if (normalized.includes('data and hash arguments')) return true;

  const url = String(e.config?.url || e.message || '');
  const isLoginRequest =
    url.includes('/auth/login') || url.includes('auth/login');

  const status = Number(e.response?.status || 0);
  if (isLoginRequest && status >= 500) return true;

  // Axios wrapper when response body was not parsed
  if (isLoginRequest && e.message?.includes('status code 5')) return true;

  return false;
}

export function sanitizeAuthErrorMessage(message: string | undefined): string {
  if (!message) return '';
  const lower = message.toLowerCase();
  if (lower.includes('something went very wrong')) return '';
  if (lower.includes('illegal arguments')) return '';
  return message;
}

export type ForgotPasswordErrorKind = 'email_unavailable' | 'not_found' | 'unknown';

function readApiErrorMessage(err: unknown): string {
  if (!err || typeof err !== 'object') return '';
  const e = err as {
    message?: string;
    response?: { data?: { message?: string } | string };
  };
  const data = e.response?.data;
  if (typeof data === 'string') return data;
  if (data && typeof data === 'object' && data.message) return String(data.message);
  return String(e.message || '');
}

/** Classify forgot-password API failures (no backend changes). */
export function classifyForgotPasswordError(err: unknown): ForgotPasswordErrorKind {
  const status = Number((err as { response?: { status?: number } })?.response?.status || 0);
  const message = readApiErrorMessage(err).toLowerCase();
  const url = String((err as { config?: { url?: string } })?.config?.url || '');
  const isForgotRequest =
    url.includes('/auth/forgot-password') || url.includes('forgot-password');

  if (status === 404 || message.includes('user not found')) {
    return 'not_found';
  }

  if (
    status >= 500 ||
    status === 503 ||
    message.includes('something went very wrong') ||
    message.includes('unable to send reset email') ||
    message.includes('email service is not configured') ||
    message.includes('email template not found') ||
    message.includes('status code 5') ||
    (isForgotRequest && status === 0 && message.includes('network'))
  ) {
    return 'email_unavailable';
  }

  return 'unknown';
}

/** Treat masked production 500 responses as email delivery failures on forgot-password. */
export function isForgotPasswordEmailFailure(err: unknown): boolean {
  return classifyForgotPasswordError(err) === 'email_unavailable';
}

export function getBuyerForgotPasswordUrl(buyerSiteUrl: string): string {
  const base = buyerSiteUrl.replace(/\/+$/, '');
  return `${base}/forgot-password`;
}

export function saveGooglePasswordPrompt(email: string): void {
  try {
    sessionStorage.setItem(GOOGLE_PASSWORD_PROMPT_KEY, email);
  } catch {
    /* ignore */
  }
}

export function loadGooglePasswordPrompt(): string | null {
  try {
    return sessionStorage.getItem(GOOGLE_PASSWORD_PROMPT_KEY);
  } catch {
    return null;
  }
}

export function clearGooglePasswordPrompt(): void {
  try {
    sessionStorage.removeItem(GOOGLE_PASSWORD_PROMPT_KEY);
  } catch {
    /* ignore */
  }
}
